<template>
  <v-container fluid class="receiving-page pa-3 pa-sm-5">
    <div class="mobile-shell">
      <div class="brand-bar">
        <div class="brand-mark">
          <v-icon color="white">mdi-package-variant-closed-check</v-icon>
        </div>
        <div>
          <div class="brand-title">
            {{ businessName || "Warehouse Receiving" }}
          </div>
          <div class="brand-subtitle">Inbound delivery collection</div>
        </div>
        <v-spacer />
        <v-btn v-if="person" icon small @click="changeAccess">
          <v-icon>mdi-logout</v-icon>
        </v-btn>
      </div>

      <v-card v-if="!person" outlined class="access-card">
        <v-card-text class="access-content">
          <div class="hero-icon">
            <v-icon size="54" color="primary">mdi-shield-key-outline</v-icon>
          </div>
          <div class="hero-title">Enter your access code</div>
          <div class="hero-text">
            Enter the private code provided by your warehouse manager.
          </div>

          <v-form class="access-form" @submit.prevent="verifyAccess">
            <v-text-field
              ref="accessCode"
              v-model.trim="accessCodeInput"
              label="Access code"
              placeholder="Enter or paste code"
              prepend-inner-icon="mdi-key-outline"
              outlined
              rounded
              autocomplete="one-time-code"
              autocapitalize="characters"
              :disabled="verifying"
              :error-messages="accessError ? [accessError] : []"
            />
            <v-btn
              block
              x-large
              rounded
              depressed
              color="primary"
              dark
              type="submit"
              :loading="verifying"
              :disabled="!accessCodeInput"
            >
              Continue
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>

      <v-card v-else-if="completed" outlined class="success-card">
        <v-card-text class="success-content">
          <div class="success-icon">
            <v-icon size="68" color="success">mdi-check-circle</v-icon>
          </div>
          <div class="hero-title">You are all set!</div>
          <div class="hero-text">
            Thanks, your delivery has been submitted to the office for review.
          </div>
          <div v-if="submittedReference" class="reference-chip">
            {{ submittedReference }}
          </div>
          <v-btn
            block
            x-large
            rounded
            depressed
            color="primary"
            dark
            class="mt-7"
            @click="startAnother"
          >
            Start Another Delivery
          </v-btn>
        </v-card-text>
      </v-card>

      <v-card v-else-if="!started" outlined class="start-card">
        <v-card-text class="start-content">
          <v-avatar size="72" color="blue lighten-5">
            <v-icon size="38" color="primary">mdi-account-hard-hat-outline</v-icon>
          </v-avatar>
          <div class="welcome-label">Welcome</div>
          <div class="hero-title">{{ person.name }}</div>
          <div class="hero-text">
            Start a new delivery when you are ready to scan incoming products.
          </div>

          <v-alert
            v-if="restoredDraft"
            type="info"
            text
            dense
            class="draft-alert"
          >
            Your unfinished delivery has been restored on this device.
          </v-alert>

          <v-btn
            block
            x-large
            rounded
            depressed
            color="primary"
            dark
            class="mt-7"
            @click="startDelivery"
          >
            <v-icon left>mdi-truck-plus-outline</v-icon>
            Start New Delivery
          </v-btn>
        </v-card-text>
      </v-card>

      <template v-else>
        <div class="draft-header">
          <div>
            <div class="draft-title">New Delivery</div>
            <div class="draft-subtitle">
              {{ person.name }} · Saved on this device
            </div>
          </div>
          <div class="item-count">{{ items.length }}</div>
        </div>

        <v-alert v-if="pageError" type="error" outlined dense>
          {{ pageError }}
        </v-alert>

        <v-card outlined class="scan-action-card">
          <v-card-text class="pa-4">
            <v-btn
              block
              x-large
              rounded
              depressed
              color="primary"
              dark
              :loading="lookingUp"
              @click="openScanner"
            >
              <v-icon left size="28">mdi-barcode-scan</v-icon>
              Scan Barcode
            </v-btn>

            <div class="or-divider"><span>or enter it manually</span></div>

            <div class="manual-row">
              <v-text-field
                v-model.trim="manualCode"
                label="UPC or SKU"
                outlined
                dense
                hide-details
                @keyup.enter="lookupCode(manualCode)"
              />
              <v-btn
                color="primary"
                outlined
                height="40"
                :disabled="!manualCode"
                :loading="lookingUp"
                @click="lookupCode(manualCode)"
              >
                Find
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <div class="items-heading">
          <div>
            <div class="section-title">Scanned Items</div>
            <div class="section-subtitle">Tap an item to change its quantity.</div>
          </div>
          <v-btn v-if="items.length" text small color="primary" @click="openScanner">
            <v-icon left small>mdi-plus</v-icon>Add
          </v-btn>
        </div>

        <div v-if="items.length" class="item-list">
          <v-card
            v-for="(item, index) in items"
            :key="`${item.ProductId}-${index}`"
            outlined
            class="item-card"
            @click="editItem(index)"
          >
            <div class="item-icon">
              <v-icon color="primary">mdi-package-variant</v-icon>
            </div>
            <div class="item-info">
              <div class="item-name">{{ item.productName }}</div>
              <div class="item-code">{{ item.scannedCode }}</div>
            </div>
            <div class="quantity-box">
              <div class="quantity-value">{{ item.quantity }}</div>
              <div class="quantity-label">Boxes</div>
            </div>
            <v-btn icon small @click.stop="removeItem(index)">
              <v-icon small color="red lighten-1">mdi-delete-outline</v-icon>
            </v-btn>
          </v-card>
        </div>

        <v-card v-else outlined class="empty-items">
          <v-icon size="48" color="grey lighten-1">mdi-barcode-scan</v-icon>
          <div class="empty-title">No items scanned yet</div>
          <div class="empty-text">Scan the first product to begin this delivery.</div>
        </v-card>

        <v-textarea
          v-if="items.length"
          v-model.trim="note"
          label="Delivery note (optional)"
          outlined
          rows="2"
          auto-grow
          class="mt-4"
          @input="saveDraft"
        />

        <div class="submit-footer">
          <div class="submit-summary">
            <span>
              {{ items.length }} entr{{ items.length === 1 ? "y" : "ies" }}
            </span>
            <strong>
              {{ totalQuantity }} total box{{ totalQuantity === 1 ? "" : "es" }}
            </strong>
          </div>
          <v-btn
            block
            x-large
            rounded
            depressed
            color="success"
            dark
            :disabled="!items.length"
            :loading="submitting"
            @click="submitDelivery"
          >
            Submit Delivery
            <v-icon right>mdi-check</v-icon>
          </v-btn>
        </div>
      </template>
    </div>

    <v-dialog v-model="productDialog" persistent max-width="500px">
      <v-card class="product-dialog-card">
        <div class="product-dialog-header">
          <div>
            <div class="dialog-eyebrow">Product found</div>
            <div class="dialog-product-name">
              {{ selectedProduct ? selectedProduct.name : "" }}
            </div>
          </div>
          <v-btn icon small @click="closeProductDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="pa-4">
          <div class="product-code-row">
            <span>{{ selectedProduct ? selectedProduct.scannedCode : "" }}</span>
            <v-chip small color="blue lighten-5" text-color="primary">
              {{ selectedProduct ? selectedProduct.scanType.toUpperCase() : "" }}
            </v-chip>
          </div>
          <div class="quantity-question">How many boxes did you receive?</div>
          <v-text-field
            ref="quantity"
            v-model.number="quantity"
            type="number"
            min="0.01"
            step="1"
            label="Number of boxes"
            outlined
            autofocus
            class="quantity-input"
            :error-messages="quantityError ? [quantityError] : []"
            @focus="$event.target.select()"
            @keyup.enter="saveItem"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="closeProductDialog">Cancel</v-btn>
          <v-btn rounded depressed color="primary" dark @click="saveItem">
            Save Item
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="scannerDialog"
      persistent
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card class="scanner-dialog">
        <div class="camera-header">
          <div>
            <div class="camera-title">Scan Product</div>
            <div class="camera-subtitle">Place the barcode inside the frame.</div>
          </div>
          <v-btn icon dark @click="closeScanner"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <div class="camera-content">
          <div class="camera-box">
            <div id="barcode-reader"></div>
            <div class="scan-guide"></div>
          </div>
          <div class="camera-help">Hold the phone steady and keep the barcode well lit.</div>
          <v-progress-circular
            v-if="startingCamera"
            indeterminate
            color="white"
            class="mt-5"
          />
        </div>
        <div class="camera-footer">
          <v-btn block large rounded outlined dark @click="closeScanner">Cancel</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import service from "../inboundDeliveryService";

export default {
  name: "InboundDeliveryScanner",
  data() {
    return {
      accessCodeInput: "",
      accessCode: "",
      accessError: "",
      verifying: false,
      person: null,
      started: false,
      restoredDraft: false,
      completed: false,
      submittedReference: "",
      items: [],
      note: "",
      manualCode: "",
      pageError: "",
      lookingUp: false,
      submitting: false,
      productDialog: false,
      selectedProduct: null,
      editingIndex: -1,
      quantity: 1,
      quantityError: "",
      scannerDialog: false,
      scanner: null,
      startingCamera: false,
      scanLocked: false,
    };
  },
  computed: {
    businessName() {
      return this.person && this.person.business ? this.person.business.name : "";
    },
    totalQuantity() {
      return this.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    },
    draftKey() {
      return `smpl-inbound-delivery-draft:${this.accessCode}`;
    },
  },
  async created() {
    const code = this.$route.params.code || this.$route.query.code;
    if (code) {
      this.accessCodeInput = String(code);
      await this.verifyAccess();
    }
  },
  beforeDestroy() {
    this.stopScanner();
    this.unlockOrientation();
  },
  methods: {
    responseData(response) {
      return response && response.data !== undefined ? response.data : response;
    },
    errorMessage(error, fallback) {
      return error && error.data && error.data.message ? error.data.message : fallback;
    },
    async verifyAccess() {
      const code = this.accessCodeInput.trim();
      if (!code) return;
      this.verifying = true;
      this.accessError = "";
      try {
        const response = await service.verify(code);
        this.person = this.responseData(response);
        this.accessCode = code;
        this.accessCodeInput = code;
        window.history.replaceState(
          {},
          "",
          `/p/delivery/${encodeURIComponent(code)}`
        );
        this.restoreDraft();
      } catch (error) {
        this.person = null;
        this.accessError = this.errorMessage(error, "Access code is invalid or disabled.");
      } finally {
        this.verifying = false;
      }
    },
    changeAccess() {
      this.person = null;
      this.accessCode = "";
      this.accessCodeInput = "";
      this.started = false;
      this.items = [];
      this.note = "";
      this.completed = false;
      window.history.replaceState({}, "", "/p/delivery");
      this.$nextTick(() => this.$refs.accessCode && this.$refs.accessCode.focus());
    },
    restoreDraft() {
      this.restoredDraft = false;
      try {
        const saved = JSON.parse(localStorage.getItem(this.draftKey));
        if (saved && saved.started) {
          this.items = Array.isArray(saved.items) ? saved.items : [];
          this.note = saved.note || "";
          this.started = true;
          this.restoredDraft = true;
        }
      } catch (error) {
        localStorage.removeItem(this.draftKey);
      }
    },
    saveDraft() {
      if (!this.accessCode || !this.started) return;
      localStorage.setItem(
        this.draftKey,
        JSON.stringify({ started: true, items: this.items, note: this.note })
      );
    },
    startDelivery() {
      this.started = true;
      this.completed = false;
      this.restoredDraft = false;
      this.saveDraft();
    },
    startAnother() {
      this.completed = false;
      this.started = false;
      this.items = [];
      this.note = "";
      this.submittedReference = "";
    },
    async lookupCode(rawCode) {
      const code = String(rawCode || "").trim();
      if (!code || this.lookingUp) return;
      this.lookingUp = true;
      this.pageError = "";
      try {
        const response = await service.findProduct(this.accessCode, code);
        const product = this.responseData(response);
        this.manualCode = "";
        this.openProduct(product);
      } catch (error) {
        this.pageError = this.errorMessage(error, `No product found for ${code}.`);
      } finally {
        this.lookingUp = false;
      }
    },
    openProduct(product) {
      this.selectedProduct = product;
      // Every scan represents a separate pallet or receiving entry, even if
      // the same product was scanned earlier. Existing entries are edited only
      // when their individual card is tapped.
      this.editingIndex = -1;
      this.quantity = 1;
      this.quantityError = "";
      this.productDialog = true;
      this.$nextTick(this.selectQuantity);
    },
    editItem(index) {
      const item = this.items[index];
      this.selectedProduct = {
        id: item.ProductId,
        name: item.productName,
        scannedCode: item.scannedCode,
        scanType: item.scanType,
        unitCountInBox: item.unitCountInBox,
      };
      this.editingIndex = index;
      this.quantity = item.quantity;
      this.quantityError = "";
      this.productDialog = true;
      this.$nextTick(this.selectQuantity);
    },
    selectQuantity() {
      const field = this.$refs.quantity;
      const input = field && field.$el ? field.$el.querySelector("input") : null;
      if (input) {
        input.focus();
        input.select();
      }
    },
    closeProductDialog() {
      this.productDialog = false;
      this.selectedProduct = null;
      this.editingIndex = -1;
    },
    saveItem() {
      const quantity = Number(this.quantity);
      if (!Number.isFinite(quantity) || quantity <= 0) {
        this.quantityError = "Quantity must be greater than zero.";
        return;
      }
      const item = {
        ProductId: this.selectedProduct.id,
        productName: this.selectedProduct.name,
        scannedCode: this.selectedProduct.scannedCode,
        scanType: this.selectedProduct.scanType,
        unitCountInBox: this.selectedProduct.unitCountInBox,
        quantity,
      };
      if (this.editingIndex >= 0) this.$set(this.items, this.editingIndex, item);
      else this.items.push(item);
      this.saveDraft();
      this.closeProductDialog();
    },
    removeItem(index) {
      this.items.splice(index, 1);
      this.saveDraft();
    },
    async submitDelivery() {
      if (!this.items.length) return;
      const confirmation = await this.$swal({
        title: "Submit delivery?",
        text: `Send ${this.items.length} entr${this.items.length === 1 ? "y" : "ies"} and ${this.totalQuantity} boxes to the office for review.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Submit",
      });
      if (!confirmation.value) return;
      this.submitting = true;
      this.pageError = "";
      try {
        const response = await service.submit(this.accessCode, {
          note: this.note,
          details: this.items,
        });
        const delivery = this.responseData(response);
        this.submittedReference = delivery.referenceNumber || "";
        localStorage.removeItem(this.draftKey);
        this.items = [];
        this.note = "";
        this.started = false;
        this.completed = true;
      } catch (error) {
        this.pageError = this.errorMessage(error, "Unable to submit this delivery. Please try again.");
      } finally {
        this.submitting = false;
      }
    },
    async lockPortraitOrientation() {
      try {
        if (screen.orientation && typeof screen.orientation.lock === "function") {
          await screen.orientation.lock("portrait");
        }
      } catch (error) {
        console.warn("Portrait orientation lock is not supported", error);
      }
    },
    unlockOrientation() {
      try {
        if (screen.orientation && typeof screen.orientation.unlock === "function") {
          screen.orientation.unlock();
        }
      } catch (error) {
        console.warn("Unable to unlock orientation", error);
      }
    },
    async openScanner() {
      this.pageError = "";
      this.scanLocked = false;
      this.scannerDialog = true;
      await this.$nextTick();
      await this.lockPortraitOrientation();
      await this.startScanner();
    },
    async startScanner() {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.pageError = "Camera access is not supported by this browser.";
        this.scannerDialog = false;
        return;
      }
      this.startingCamera = true;
      try {
        if (this.scanner) await this.stopScanner();
        this.scanner = new Html5Qrcode("barcode-reader", {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.CODE_93,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.ITF,
            Html5QrcodeSupportedFormats.CODABAR,
            Html5QrcodeSupportedFormats.QR_CODE,
          ],
          verbose: false,
        });
        await this.scanner.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: (width, height) => ({
              width: Math.floor(width * 0.86),
              height: Math.min(Math.floor(height * 0.4), 180),
            }),
            aspectRatio: 1.333334,
            experimentalFeatures: { useBarCodeDetectorIfSupported: true },
          },
          this.onScanSuccess,
          () => {}
        );
      } catch (error) {
        this.pageError = this.getCameraErrorMessage(error);
        this.scannerDialog = false;
        await this.stopScanner();
      } finally {
        this.startingCamera = false;
      }
    },
    async onScanSuccess(decodedText) {
      if (this.scanLocked) return;
      this.scanLocked = true;
      if (navigator.vibrate) navigator.vibrate(120);
      await this.closeScanner();
      await this.lookupCode(decodedText);
    },
    async closeScanner() {
      await this.stopScanner();
      this.unlockOrientation();
      this.scannerDialog = false;
      this.startingCamera = false;
    },
    async stopScanner() {
      if (!this.scanner) return;
      try {
        if (this.scanner.isScanning) await this.scanner.stop();
        this.scanner.clear();
      } catch (error) {
        console.warn("Unable to stop barcode scanner", error);
      } finally {
        this.scanner = null;
      }
    },
    getCameraErrorMessage(error) {
      const name = error && error.name ? error.name : "";
      const message = error && error.message ? error.message : "";
      if (name === "NotAllowedError" || /permission/i.test(message))
        return "Camera permission was denied. Allow camera access and try again.";
      if (name === "NotFoundError" || /not found/i.test(message))
        return "No camera was found on this device.";
      if (!window.isSecureContext) return "Camera access requires a secure HTTPS address.";
      return "The camera could not start. Close other camera apps and try again.";
    },
  },
};
</script>

<style scoped>
.receiving-page { min-height: 100vh; background: #f1f5f9; }
.mobile-shell { width: 100%; max-width: 620px; margin: 0 auto; }
.brand-bar { display: flex; align-items: center; gap: 11px; padding: 8px 4px 18px; }
.brand-mark { width: 42px; height: 42px; border-radius: 14px; background: #2563eb; display: flex; align-items: center; justify-content: center; }
.brand-title { color: #0f172a; font-size: 16px; font-weight: 900; }
.brand-subtitle { color: #64748b; font-size: 11px; }
.access-card, .start-card, .success-card { border-radius: 24px !important; background: #fff; overflow: hidden; }
.access-content, .start-content, .success-content { min-height: calc(100vh - 125px); padding: 34px 22px !important; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
.hero-icon, .success-icon { width: 96px; height: 96px; border-radius: 28px; background: #eff6ff; display: flex; align-items: center; justify-content: center; margin-bottom: 22px; }
.success-icon { background: #ecfdf5; }
.hero-title { color: #0f172a; font-size: 25px; line-height: 1.2; font-weight: 900; }
.hero-text { color: #64748b; font-size: 14px; line-height: 1.6; max-width: 390px; margin-top: 9px; }
.access-form { width: 100%; margin-top: 30px; }
.welcome-label { color: #2563eb; font-size: 12px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; margin-top: 18px; }
.draft-alert { width: 100%; margin-top: 22px; text-align: left; }
.reference-chip { background: #f1f5f9; border-radius: 12px; color: #475569; font-family: monospace; font-weight: 800; padding: 9px 14px; margin-top: 20px; }
.draft-header { background: #fff; border: 1px solid #e2e8f0; border-radius: 20px; padding: 16px; display: flex; align-items: center; margin-bottom: 12px; }
.draft-title { color: #0f172a; font-size: 22px; font-weight: 900; }
.draft-subtitle { color: #64748b; font-size: 12px; margin-top: 2px; }
.item-count { margin-left: auto; width: 40px; height: 40px; border-radius: 14px; background: #eff6ff; color: #2563eb; display: flex; align-items: center; justify-content: center; font-size: 18px; font-weight: 900; }
.scan-action-card { border-radius: 20px !important; background: #fff; }
.or-divider { display: flex; align-items: center; color: #94a3b8; font-size: 11px; margin: 16px 0; }
.or-divider::before, .or-divider::after { content: ""; height: 1px; background: #e2e8f0; flex: 1; }
.or-divider span { padding: 0 10px; }
.manual-row { display: flex; gap: 9px; align-items: flex-start; }
.items-heading { display: flex; align-items: center; justify-content: space-between; padding: 20px 4px 10px; }
.section-title { color: #0f172a; font-size: 17px; font-weight: 900; }
.section-subtitle { color: #64748b; font-size: 11px; }
.item-list { display: grid; gap: 9px; }
.item-card { border-radius: 17px !important; padding: 12px; display: flex; align-items: center; gap: 10px; background: #fff; cursor: pointer; }
.item-icon { width: 42px; height: 42px; border-radius: 13px; background: #eff6ff; display: flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.item-info { min-width: 0; flex: 1; }
.item-name { color: #0f172a; font-size: 14px; font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.item-code { color: #64748b; font-family: monospace; font-size: 11px; margin-top: 2px; }
.quantity-box { min-width: 46px; text-align: center; }
.quantity-value { color: #0f172a; font-size: 18px; font-weight: 900; }
.quantity-label { color: #94a3b8; font-size: 9px; text-transform: uppercase; }
.empty-items { border-radius: 20px !important; padding: 34px 16px; text-align: center; background: #fff; }
.empty-title { color: #0f172a; font-size: 16px; font-weight: 800; margin-top: 9px; }
.empty-text { color: #64748b; font-size: 12px; margin-top: 3px; }
.submit-footer { background: #fff; border: 1px solid #e2e8f0; border-radius: 20px; padding: 14px; margin-top: 14px; margin-bottom: calc(8px + env(safe-area-inset-bottom)); }
.submit-summary { display: flex; justify-content: space-between; color: #64748b; font-size: 12px; padding: 0 3px 11px; }
.submit-summary strong { color: #0f172a; }
.product-dialog-card { border-radius: 22px; overflow: hidden; }
.product-dialog-header { padding: 18px 20px; border-bottom: 1px solid #e2e8f0; display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.dialog-eyebrow { color: #2563eb; font-size: 11px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; }
.dialog-product-name { color: #0f172a; font-size: 20px; font-weight: 900; margin-top: 3px; }
.product-code-row { display: flex; align-items: center; justify-content: space-between; background: #f8fafc; border-radius: 13px; padding: 11px; color: #475569; font-family: monospace; }
.quantity-question { color: #334155; font-size: 14px; font-weight: 700; margin: 22px 0 9px; }
.quantity-input::v-deep input { text-align: center; font-size: 26px; font-weight: 900; }
.scanner-dialog { min-height: 100vh; background: #020617; display: flex; flex-direction: column; }
.camera-header { min-height: 74px; padding: calc(12px + env(safe-area-inset-top)) 16px 12px; display: flex; align-items: center; justify-content: space-between; background: #0f172a; color: #fff; }
.camera-title { font-size: 19px; font-weight: 800; }
.camera-subtitle, .camera-help { color: #cbd5e1; font-size: 12px; }
.camera-content { flex: 1; padding: 20px 14px; display: flex; flex-direction: column; align-items: center; justify-content: center; }
.camera-box { width: 100%; max-width: 700px; min-height: 300px; border-radius: 18px; overflow: hidden; background: #000; position: relative; }
#barcode-reader { width: 100%; min-height: 300px; }
#barcode-reader::v-deep video { width: 100% !important; min-height: 300px; object-fit: cover; }
.scan-guide { position: absolute; inset: 20% 8%; border: 3px solid #60a5fa; border-radius: 18px; pointer-events: none; box-shadow: 0 0 0 2000px rgba(2, 6, 23, .35); }
.camera-help { margin-top: 18px; text-align: center; }
.camera-footer { padding: 14px 16px calc(14px + env(safe-area-inset-bottom)); background: #0f172a; }
@media (max-width: 430px) {
  .receiving-page { padding: 8px !important; }
  .brand-bar { padding: 6px 3px 10px; }
  .access-content, .start-content, .success-content { min-height: calc(100vh - 88px); padding: 26px 18px !important; }
  .hero-title { font-size: 23px; }
}
</style>
